// ** React Imports
import { Fragment, useEffect, useState } from 'react'
//import Typography, {  } from '@mui/material/Typography'
import {
    Box,
    Typography,
    TypographyProps,
    styled,
    Grid,
    Tooltip,
    ImageListItem,
    ImageListItemBar,
    IconButton,
    Badge,
    Avatar,
} from '@mui/material';
import VideoThumbnail from './VideoThumbnail';

// ** Third Party Components
import { Icon } from '@iconify/react';

import toast from 'react-hot-toast'
import { useDropzone, type Accept } from 'react-dropzone'
import DropzoneWrapper from 'src/@core/styles/libs/react-dropzone'
import { ChannelType } from '../types/apps/channelTypes'
import PermMediaIcon from '@mui/icons-material/PermMedia';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';


interface FileProp {
    file: File;
    orgFn: string;
    size?: number;
    [key: string]: any;
}

interface ParentProp {
    updateFiles: (files: FileProp[]) => void;
    //channelType: string,
    selectedChannels: ChannelType[];
    files: FileProp[];
    handleRemoveFile: (file: FileProp) => void;
    videoImage: string;
    disabled?: boolean;
}

enum AllowedType {
    VideoOnly = "videoonly",
    VideoAndImage = "videoandimage",
    All = "all"
}

// Styled component for the upload icon inside the dropzone area
const StyledIcon = styled(PermMediaIcon)(({ theme }) => ({
    fontSize: 'clamp(40px, 8vw, 80px)',
    color: 'text.primary',
    [theme.breakpoints.up('md')]: {
        marginRight: theme.spacing(10),
    },
    [theme.breakpoints.down('md')]: {
        marginBottom: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: 60,
        marginBottom: theme.spacing(3),
    },
}));

const HeadingTypography = styled(Typography)<TypographyProps>(({ theme }) => ({
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(4)
    }
}));

const FileUploaderCompact = (props: ParentProp) => {

    const { updateFiles, selectedChannels, files, handleRemoveFile, videoImage, disabled = false } = props;
    const [allowedType, setAllowedType] = useState<AllowedType>(AllowedType.VideoOnly);
    const [hasX, setHasX] = useState(false); //show API limits

    useEffect(() => { })

    useEffect(() => {
        const determineAllowedType = (): AllowedType => {
            const mediumsPresent = selectedChannels.map(c => c.mediumName.toLowerCase());
            const hasGoogle = mediumsPresent.includes("google");
            const hasFacebook = mediumsPresent.includes("facebook");
            const hasPinterest = mediumsPresent.includes("pinterest");
            const hasInstagram = mediumsPresent.includes("instagram");
            const hasTikTok = mediumsPresent.includes("tiktok");
            const hasThreads = mediumsPresent.includes("threads");
            const hasLinkedIn = mediumsPresent.includes("linkedin");
            const hasX = mediumsPresent.includes("x") || mediumsPresent.includes("twitter");
            setHasX(hasX);

            if (hasX) {
                return AllowedType.All;
            } else if (hasGoogle) {
                return AllowedType.VideoOnly;
            }
            else if (hasInstagram) {
                return AllowedType.VideoAndImage;
            }
            else if (hasFacebook) {
                if (hasPinterest) {
                    return AllowedType.VideoAndImage;
                }
                else {
                    return AllowedType.All;
                }
            }
            else if (hasPinterest) {
                return AllowedType.VideoAndImage;
            }
            else if (hasTikTok) {
                return AllowedType.VideoAndImage;
            }
            else if (hasThreads) {
                return AllowedType.VideoAndImage;
            }
            else if (hasLinkedIn) {
                return AllowedType.VideoAndImage;
            }
            return AllowedType.VideoAndImage;
        };

        setAllowedType(determineAllowedType());
    }, [selectedChannels]);

    const isX = hasX;

    const getMime = (fp: FileProp) => fp.file?.type || '';
    const getName = (fp: FileProp) => fp.orgFn || fp.file?.name || '';
    const isGif = (fp: FileProp) => getMime(fp) === 'image/gif' || getName(fp).toLowerCase().endsWith('.gif');
    const isVideo = (fp: FileProp) => getMime(fp).startsWith('video/');
    const isImage = (fp: FileProp) => getMime(fp).startsWith('image/') && !isGif(fp);

    const existingHasVideo = isX && files?.some(isVideo);
    const existingHasGif = isX && files?.some(isGif);
    const existingHasImages = isX && files?.some(isImage);

    const xMaxFiles = isX ? ((existingHasVideo || existingHasGif) ? 1 : 4) : undefined;

    const xAccept: Accept =
        existingHasVideo
            ? { 'video/*': ['.mov', '.mp4', '.m4v', '.avi', '.webm'] }
            : existingHasGif
                ? { 'image/gif': ['.gif'] }
                : {
                    'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
                    'video/*': ['.mov', '.mp4', '.m4v', '.avi', '.webm'],
                };

    const defaultAccept: Accept =
        allowedType === AllowedType.VideoOnly
            ? { 'video/*': ['.mov', '.mp4', '.m4v', '.avi', '.webm'] }
            : {
                'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
                'video/*': ['.mov', '.mp4', '.m4v', '.avi', '.webm'],
            };

    const { getRootProps, getInputProps } = useDropzone({
        disabled,
        maxFiles: isX ? (xMaxFiles ?? 4) : (allowedType === AllowedType.VideoOnly ? 1 : 5),
        maxSize: 52000000,
        accept: isX ? xAccept : defaultAccept,

        onDrop: (acceptedFiles: File[]) => {
            const newFiles = acceptedFiles.map((file) => ({ file, orgFn: file.name, size: file.size }));

            if (!isX) {
                updateFiles(newFiles);
                return;
            }

            const keyOf = (fp: FileProp) => fp.folderRefId ? `lib:${fp.folderRefId}:${fp.orgFn}` : `file:${fp.file.name}:${fp.file.size}:${fp.file.lastModified}`;

            const merged = [...(files ?? []), ...newFiles];
            const deduped: FileProp[] = [];
            const seen = new Set<string>();

            for (const f of merged) {
                const k = keyOf(f);
                if (!seen.has(k)) {
                    seen.add(k);
                    deduped.push(f);
                }
            }

            const videoCount = deduped.filter(isVideo).length;
            const gifCount = deduped.filter(isGif).length;
            const imageCount = deduped.filter(isImage).length;
            const total = deduped.length;

            const invalid =
                (videoCount > 0 && (total !== 1 || gifCount > 0 || imageCount > 0)) ||
                (gifCount > 0 && (total !== 1 || videoCount > 0 || imageCount > 0)) ||
                (videoCount === 0 && gifCount === 0 && (imageCount !== total || total > 4));

            if (invalid) {
                toast.error('X limits: up to 4 photos, or 1 GIF, or 1 video. No mixing.', { duration: 2200 });
                return;
            }

            updateFiles(deduped);
        },
        onDropRejected: () => {
            if (isX) {
                toast.error('X limits: up to 4 photos, or 1 GIF, or 1 video. Max size 50 MB.', { duration: 2200 });
                return;
            }

            if (allowedType === AllowedType.VideoOnly) {
                toast.error('You can only upload 1 video file from supported file types & maximum size of 50 MB.', { duration: 2000 });
            } else {
                toast.error('You can only upload 5 files & maximum size of 50 MB.', { duration: 2000 });
            }
        }
    })

    return (
        <DropzoneWrapper>
            <Box
                {...getRootProps({ className: 'dropzone' })}
                sx={{
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    p: 3,
                    position: 'relative',
                    opacity: disabled ? 0.6 : 1,
                    pointerEvents: disabled ? 'none' : 'auto',
                }}
            >
                <input {...getInputProps()} disabled={disabled} />
                <Grid
                    container
                    spacing={2}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ minHeight: { xs: 150, sm: 200 } }}                 >

                    <Grid item xs={12} sm="auto" sx={{ display: 'flex', justifyContent: 'center' }}>
                        <StyledIcon />
                    </Grid>

                    <Grid item xs={12} sm="auto" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                        <HeadingTypography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            Drop files here or click to upload.
                        </HeadingTypography>
                        {allowedType === AllowedType.VideoOnly && (
                            <Typography color="textSecondary" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                Allowed *.mov, *.mp4, *.m4v, *.avi
                            </Typography>
                        )}
                        {allowedType !== AllowedType.VideoOnly && (
                            <Typography color="textSecondary" variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                Allowed *.gif, *.jpg, *.jpeg, *.png, *.mov, *.mp4, *.m4v, *.avi
                            </Typography>
                        )}
                        {isX && (
                            <Box
                                sx={{
                                    mt: 1.25,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: 999,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: 'action.hover',
                                }}
                            >
                                <Icon icon="ri:twitter-x-fill" width={14} height={14} />
                                <Typography variant="caption" color="text.secondary">
                                    X limits: 4 photos • or 1 GIF • or 1 video
                                </Typography>
                            </Box>
                        )}

                    </Grid>
                </Grid>


                {/* Display Selected Files */}
                {files && files.length > 0 && (
                    <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            width: "100%",
                            mt: 2,
                            p: 2,

                            backgroundColor: 'background.paper',
                            justifyContent: 'flex-start',

                        }}
                    >
                        {files.map((fileObj: FileProp, index: number) => (
                            <Box
                                key={index}
                                sx={{
                                    width: 100,
                                    height: 100,
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    m: 1,
                                    position: 'relative',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                }}
                            >
                                {fileObj.file.type.startsWith('image') ? fileObj.orgFn && fileObj.folderRefId ? 
                                    <Tooltip title={fileObj.file.name} arrow>
                                        <img
                                            alt={fileObj.file.name}
                                            src={`\\image\\PublicMedia?fileReference=${fileObj.orgFn}&folderPath=${fileObj.folderRefId}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: 8,
                                            }}
                                        />

                                    </Tooltip>
                                : (
                                    <Tooltip title={fileObj.file.name} arrow>
                                        <img
                                            alt={fileObj.file.name}
                                            src={URL.createObjectURL(fileObj.file)}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: 8,
                                            }}
                                        />

                                    </Tooltip>
                                ) : (
                                    <VideoThumbnail file={fileObj.file} />
                                )}
                                <IconButton
                                    onClick={() => handleRemoveFile(fileObj)}
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0,0,0,0.8)',
                                        },
                                    }}
                                    size="small"
                                    disabled={disabled}
                                >
                                    <Icon icon="mdi:close" width={16} height={16} />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </DropzoneWrapper>
    )
}

export default FileUploaderCompact;
